/**
 * Feature flags.
 *
 * creatorV2 — the redesigned creation flow (Phase 8). On for everyone since the
 * flip (08-07, 2026-09-26). `?creator=v1` stays as an escape hatch back to the
 * old CreatorWizard for two weeks; after that the old creators and this flag are
 * deleted. Rolling back is a one-line change to CREATOR_V2_DEFAULT.
 *
 * Resolution order:
 *   1. `?creator=v2` / `?creator=v1` in the URL — wins, and is remembered, so a
 *      tester opens one link once and stays on that creator.
 *   2. The remembered choice in localStorage (`puzzly_creator`).
 *   3. The default: CREATOR_V2_DEFAULT (the admin allowlist was on before the flip).
 *
 * Storage can throw (Safari private mode, blocked site data); every access is
 * guarded so a flag lookup can never break rendering.
 */

// On for everyone since 08-07. Set back to false to roll the flip back.
export const CREATOR_V2_DEFAULT = true;

const PARAM = "creator";
const STORAGE_KEY = "puzzly_creator";

function readParam(search) {
  try {
    const value = new URLSearchParams(search || "").get(PARAM);
    return value === "v1" || value === "v2" ? value : null;
  } catch (_) {
    return null;
  }
}

function readStored(storage) {
  try {
    const value = storage ? storage.getItem(STORAGE_KEY) : null;
    return value === "v1" || value === "v2" ? value : null;
  } catch (_) {
    return null;
  }
}

function remember(storage, value) {
  try {
    if (storage) storage.setItem(STORAGE_KEY, value);
  } catch (_) {
    /* the choice just won't persist — the URL still decides this visit */
  }
}

/**
 * @param {{ search?: string, storage?: Storage|null, isAdmin?: boolean }} opts
 * @returns {boolean} true when the new creator should render
 */
export function isCreatorV2Enabled({ search = "", storage = null, isAdmin = false } = {}) {
  const fromUrl = readParam(search);
  if (fromUrl) {
    remember(storage, fromUrl);
    return fromUrl === "v2";
  }
  const stored = readStored(storage);
  if (stored) return stored === "v2";
  return CREATOR_V2_DEFAULT || isAdmin;
}

/**
 * Which screen reopens a saved draft. The new creator resumes every draft,
 * Quick Puzzle ones included (D9); only on the ?creator=v1 escape hatch does a
 * quick draft go back to QuickPuzzleCreator. Deleted together with the flag.
 * @param {string} creatorType "full" | "quick"
 * @param {boolean} creatorV2
 */
export function draftResumeScreen(creatorType, creatorV2) {
  return !creatorV2 && creatorType === "quick" ? "quick_create" : "creator";
}
